import { ref } from 'vue'
import { defineStore } from 'pinia'
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  deleteDoc,
  updateDoc,
  onSnapshot,
  serverTimestamp,
} from 'firebase/firestore'
import { db } from '@/firebase'
import { useAuthStore } from './auth'
import type { Assignment } from '@/types'

const DEFAULT_POLL_INTERVAL_MS = 2 * 60 * 1000
const HEARTBEAT_INTERVAL_MS = 30_000
const SESSION_STALE_MS = 90_000
const STALE_CHECK_INTERVAL_MS = 15_000

// One stable session ID per browser tab
const _sessionId = crypto.randomUUID()

export const useCaltopoStore = defineStore('caltopo', () => {
  const isSyncing = ref(false)
  const isPaused = ref(false)
  const isLeader = ref(false)
  const lastSyncedAt = ref<Date | null>(null)
  const error = ref<string | null>(null)

  let _intervalId: ReturnType<typeof setInterval> | null = null
  let _pollParams: { incidentId: string; opId: string; mapId: string; caltopoOpId: string | null; pollIntervalMs: number } | null = null

  let _presenceUnsub: (() => void) | null = null
  let _heartbeatTimer: ReturnType<typeof setInterval> | null = null
  let _staleCheckTimer: ReturnType<typeof setInterval> | null = null
  let _currentSession: { incidentId: string; opId: string } | null = null
  // Raw heartbeat timestamps from the last snapshot, re-evaluated on a timer
  // to detect stale leaders even when Firestore emits no new changes.
  let _cachedPresence: { session_id: string; last_heartbeat_ms: number }[] = []

  // ── Session / leader election ─────────────────────────────────────────────

  /** Join the CalTopo session for this incident-op. The tab with the smallest
   *  session_id among live heartbeats becomes the leader and runs the poll. */
  function joinSession(
    incidentId: string,
    opId: string,
    mapId: string,
    caltopoOpId: string | null,
    pollIntervalMs: number = DEFAULT_POLL_INTERVAL_MS,
  ) {
    leaveSession()

    _currentSession = { incidentId, opId }
    const presenceCol = collection(db, 'incidents', incidentId, 'operational_periods', opId, 'caltopo_sessions')
    const myRef = doc(presenceCol, _sessionId)

    const _writeHeartbeat = () =>
      setDoc(myRef, { session_id: _sessionId, last_heartbeat: serverTimestamp() }).catch(() => {})

    _writeHeartbeat()
    _heartbeatTimer = setInterval(_writeHeartbeat, HEARTBEAT_INTERVAL_MS)

    function _evaluateLeadership() {
      const now = Date.now()
      const live = _cachedPresence
        .filter((s) => now - s.last_heartbeat_ms < SESSION_STALE_MS)
        .map((s) => s.session_id)
        .sort()

      const amLeader = live[0] === _sessionId
      isLeader.value = amLeader

      if (amLeader && _intervalId === null) {
        console.log('[CalTopo] became leader — starting poll', { incidentId, opId, mapId })
        _startPolling(incidentId, opId, mapId, caltopoOpId, pollIntervalMs)
      } else if (!amLeader && _intervalId !== null) {
        console.log('[CalTopo] lost leadership — stopping poll')
        _stopPolling()
      }
    }

    _presenceUnsub = onSnapshot(presenceCol, (snap) => {
      _cachedPresence = snap.docs.map((d) => ({
        session_id: d.data().session_id as string,
        last_heartbeat_ms: (d.data().last_heartbeat?.toMillis?.() ?? 0) as number,
      }))
      _evaluateLeadership()
    })

    // Re-evaluate every 15 s using cached timestamps so an abruptly-closed
    // leader tab (no deleteDoc) is detected without waiting for a new snapshot.
    _staleCheckTimer = setInterval(_evaluateLeadership, STALE_CHECK_INTERVAL_MS)
  }

  /** Leave the session: stop heartbeat, unsubscribe, delete presence doc. */
  function leaveSession() {
    if (_heartbeatTimer) { clearInterval(_heartbeatTimer); _heartbeatTimer = null }
    if (_staleCheckTimer) { clearInterval(_staleCheckTimer); _staleCheckTimer = null }
    if (_presenceUnsub) { _presenceUnsub(); _presenceUnsub = null }
    _cachedPresence = []
    isLeader.value = false
    _stopPolling()

    if (_currentSession) {
      const { incidentId, opId } = _currentSession
      deleteDoc(doc(db, 'incidents', incidentId, 'operational_periods', opId, 'caltopo_sessions', _sessionId))
        .catch(() => {})
      _currentSession = null
    }
  }

  // ── Polling ───────────────────────────────────────────────────────────────

  function _startPolling(
    incidentId: string,
    opId: string,
    mapId: string,
    caltopoOpId: string | null,
    pollIntervalMs: number,
  ) {
    if (_intervalId !== null) clearInterval(_intervalId)
    _pollParams = { incidentId, opId, mapId, caltopoOpId, pollIntervalMs }

    _syncTick(incidentId, opId, mapId, caltopoOpId)
    _intervalId = setInterval(() => {
      _syncTick(incidentId, opId, mapId, caltopoOpId)
    }, pollIntervalMs)
  }

  function _stopPolling() {
    if (_intervalId !== null) { clearInterval(_intervalId); _intervalId = null }
    _pollParams = null
  }

  /** Immediately trigger a sync tick outside the normal polling interval.
   *  Pass forceFullSync=true to reset the cursor to 0 (full re-sync).
   *  No-op for non-leaders (no _pollParams). */
  function manualSync(forceFullSync = false) {
    if (!_pollParams) return
    const { incidentId, opId, mapId, caltopoOpId } = _pollParams
    _syncTick(incidentId, opId, mapId, caltopoOpId, forceFullSync ? 0 : undefined)
  }

  function togglePause() {
    isPaused.value = !isPaused.value
  }

  async function _syncTick(
    incidentId: string,
    opId: string,
    mapId: string,
    caltopoOpId: string | null,
    cursorOverride?: number,
  ) {
    if (isSyncing.value || isPaused.value) {
      console.log('[CalTopo] _syncTick — already syncing or paused, aborting')
      return
    }

    const authStore = useAuthStore()
    const agencyId = authStore.userProfile?.agency_id
    if (!agencyId) {
      console.warn('[CalTopo] _syncTick — no agencyId, aborting')
      return
    }

    isSyncing.value = true
    lastSyncedAt.value = new Date()
    error.value = null

    try {
      // Cursor scoped to incidentId + opId + mapId so that connecting a map
      // to a new OP always triggers a full sync.
      const cursorKey = `${incidentId}_${opId}_${mapId}`
      const cursorRef = doc(db, 'agencies', agencyId, 'caltopo_cursors', cursorKey)
      const cursorSnap = await getDoc(cursorRef)
      const cursor: number = cursorOverride ?? cursorSnap.data()?.cursor ?? 0
      console.log('[CalTopo] _syncTick — mapId:', mapId, 'cursor:', cursor)

      const token = await authStore.currentUser?.getIdToken()
      const res = await fetch('/api/caltopo/sync', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ map_id: mapId, cursor, caltopo_op_id: caltopoOpId }),
      })

      if (!res.ok) {
        const body = await res.json().catch(() => ({}))
        throw new Error(body?.error?.message ?? `Sync failed: ${res.status}`)
      }

      const json = await res.json()
      const { assignments, ids, timestamp } = json.data
      console.log('[CalTopo] sync response — assignments:', assignments.length, 'timestamp:', timestamp)

      await _upsertAssignments(incidentId, opId, assignments)

      // Soft-delete detection only on full syncs (cursor === 0)
      if (cursor === 0) {
        await _softDeleteAbsentAssignments(incidentId, opId, ids['Assignment'] ?? [])
      }

      await setDoc(cursorRef, { cursor: timestamp })
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'CalTopo sync failed'
      console.error('[CalTopo] sync error:', err)
    } finally {
      isSyncing.value = false
    }
  }

  async function _upsertAssignments(
    incidentId: string,
    opId: string,
    assignments: Record<string, unknown>[],
  ) {
    if (assignments.length === 0) return

    const col = collection(db, 'incidents', incidentId, 'operational_periods', opId, 'assignments')

    console.log('[CalTopo] _upsertAssignments — incoming:', assignments.length)

    for (const assignment of assignments) {
      const calId = assignment.caltopo_assignment_id as string
      // Deterministic doc ID: same caltopo_assignment_id always maps to the
      // same Firestore doc, making concurrent creates idempotent.
      const docId = `caltopo_${calId.replace(/\//g, '_')}`
      const fields = {
        ...assignment,
        geometry: assignment.geometry ? JSON.stringify(assignment.geometry) : null,
        last_synced_at: serverTimestamp(),
        caltopo_deleted: false,
        op_id: opId,
        prev_search_efforts: (assignment.prev_search_efforts as string | undefined) ?? '',
        size_of_assignment: (assignment.size_of_assignment as string | undefined) ?? '',
      }

      try {
        await setDoc(doc(col, docId), fields)
      } catch (err) {
        console.error('[CalTopo] _upsertAssignments — write failed for', calId, err)
      }
    }
  }

  async function _softDeleteAbsentAssignments(
    incidentId: string,
    opId: string,
    caltopoIds: string[],
  ) {
    const idSet = new Set(caltopoIds)
    const col = collection(db, 'incidents', incidentId, 'operational_periods', opId, 'assignments')
    const snap = await getDocs(col)

    const toDelete = snap.docs.filter((d) => {
      const calId = d.data().caltopo_assignment_id as string | undefined
      return calId && !idSet.has(calId)
    })
    console.log('[CalTopo] _softDeleteAbsentAssignments — to soft-delete:', toDelete.length)
    await Promise.all(toDelete.map((d) => updateDoc(d.ref, { caltopo_deleted: true })))
  }

  /** Write the changed fields from a FoS assignment back to CalTopo.
   *  Fields are in FoS naming — the backend handles CalTopo translation.
   */
  async function writeBack(
    caltopoAssignmentId: string,
    mapId: string,
    fields: Partial<Assignment>,
  ) {
    console.log('[CalTopo] writeBack — caltopoId:', caltopoAssignmentId, 'mapId:', mapId)
    const authStore = useAuthStore()

    try {
      const token = await authStore.currentUser?.getIdToken()

      const { geometry, ...rest } = fields as Record<string, unknown>
      const payload = {
        map_id: mapId,
        ...rest,
        geometry: geometry ? JSON.parse(geometry as string) : null,
      }

      const res = await fetch(`/api/caltopo/assignments/${caltopoAssignmentId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      })
      if (!res.ok) {
        const body = await res.json().catch(() => ({}))
        console.error('[CalTopo] writeBack — HTTP', res.status, body)
      } else {
        console.log('[CalTopo] writeBack — success')
      }
    } catch (err) {
      console.error('[CalTopo] writeBack error:', err)
    }
  }

  return {
    isSyncing,
    isPaused,
    isLeader,
    lastSyncedAt,
    error,
    joinSession,
    leaveSession,
    manualSync,
    togglePause,
    writeBack,
  }
})
